import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Link, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import * as React from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { CinematicProvider } from "@/data/cinematicMode/context";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AppDataProvider } from "@/data/AppDataContext";
import { useOptionalAppData } from "@/data/AppDataContext";
import { WorkflowOrchestrator } from "@/components/WorkflowOrchestrator";
import { RoleProvider, type UserRole, useRole } from "@/data/RoleContext";
import { PermissionsProvider } from "@/data/PermissionsContext";
import { NAVConfig } from "@/data/navigation";
import { hasPermission } from "@/data/permissions";
import { ROLE_NAV_CONFIG } from "@/data/roleNavConfig";
import { isRouteAllowed, ROLE_DEFAULT_ROUTES } from "@/data/rolePermissions";
import { BrandingProvider } from "@/data/BrandingContext";
import { CatalogProvider } from "@/data/CatalogContext";
import { PricingProvider } from "@/data/PricingContext";
import { InventoryProvider } from "@/data/InventoryContext";
import { ProductRegistryProvider } from "@/data/ProductRegistryContext";
import { VendorPOProvider } from "@/data/VendorPOContext";
import { WarehouseInventoryProvider } from "@/data/WarehouseInventoryContext";
import { ExpenseLedgerProvider } from "@/data/jobCost/ExpenseLedgerContext";
import { StockLeakageProvider } from "@/data/stockLeakage/StockLeakageContext";
import { SafetyControlProvider } from "@/data/SafetyControlContext";
import { PipelineConfigProvider } from "@/data/PipelineConfigContext";
import { AppLayout } from "@/components/AppLayout";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Dashboard from "./pages/DashboardRoute";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import SignIn from "./pages/SignIn";
import JobsListRoute from "./pages/JobsListRoute";
import JobDetail from "./pages/JobDetailRoute";
import QAForm from "./pages/QAForm";
import SchedulingRoute from "./pages/SchedulingRoute";
import IssueForm from "./pages/IssueForm";
import IssuesListRoute from "./pages/IssuesListRoute";
import ContractorsListRoute from "./pages/ContractorsListRoute";
import ContractorProfile from "./pages/ContractorProfile";
import CustomerProfile from "./pages/CustomerProfile";
import CustomersListRoute from "./pages/CustomersListRoute";
import ContractorOnboardingRoute from "./pages/ContractorOnboardingRoute";
import ContractorIntakeRoute from "./pages/ContractorIntakeRoute";
import Reports from "./pages/ReportsRoute";
import PermittingPortalRoute from "./pages/PermittingPortalRoute";
import WarehouseReadinessRoute from "./pages/WarehouseReadinessRoute";
import StockControlRoute from "./pages/StockControlRoute";
import ProjectCloseoutRoute from "./pages/ProjectCloseoutRoute";
import InventoryModuleRoute from "./pages/InventoryModuleRoute";
import AccountingViewRoute from "./pages/AccountingViewRoute";
import { GeneralLedgerProvider } from "@/data/accounting/GeneralLedgerContext";
import { AutoPostingBridge } from "@/data/accounting/AutoPostingBridge";
import { ChangeOrderPostingBridge } from "@/data/accounting/ChangeOrderPostingBridge";
import { ProcurementPostingBridge } from "@/data/accounting/ProcurementPostingBridge";
import { InstallerReimbursementPostingBridge } from "@/data/accounting/InstallerReimbursementPostingBridge";
import { BankFeedProvider } from "@/data/accounting/bankFeed/BankFeedContext";
import { APProvider } from "@/data/accounting/ap/APContext";
import { RecurringJournalProvider } from "@/data/accounting/recurring/RecurringJournalContext";
import { FixedAssetsProvider } from "@/data/accounting/fixedAssets/FixedAssetsContext";
import { BudgetsProvider } from "@/data/accounting/budgets/BudgetsContext";
import { DocumentInboxProvider } from "@/data/accounting/documents/DocumentInboxContext";
import { AccountingTourProvider } from "@/data/accounting/tour/TourContext";
import { TourOverlay } from "@/components/accounting/tour/TourOverlay";
import { TourSpotlight } from "@/components/accounting/tour/TourSpotlight";
import { TourPromptCard } from "@/components/accounting/tour/TourPromptCard";
import GeneralLedgerHubPage from "./pages/accounting/gl/GeneralLedgerHubPage";
import ChartOfAccountsPage from "./pages/accounting/gl/ChartOfAccountsPage";
import JournalEntriesPage from "./pages/accounting/gl/JournalEntriesPage";
import TrialBalancePage from "./pages/accounting/gl/TrialBalancePage";
import GeneralLedgerDetailPage from "./pages/accounting/gl/GeneralLedgerDetailPage";
import AuditLogPage from "./pages/accounting/gl/AuditLogPage";
import RecurringJournalsPage from "./pages/accounting/gl/RecurringJournalsPage";
import JournalReviewQueuePage from "./pages/accounting/gl/JournalReviewQueuePage";
import AccountingSettingsPage from "./pages/accounting/settings/AccountingSettingsPage";
import CloseReadinessPage from "./pages/accounting/CloseReadinessPage";
import WipAgingPage from "./pages/accounting/reports/WipAgingPage";
import JobProfitabilityPage from "./pages/accounting/reports/JobProfitabilityPage";
import BankReconciliationHubPage from "./pages/accounting/bankRec/BankReconciliationHubPage";
import BankReconciliationPage from "./pages/accounting/bankRec/BankReconciliationPage";
import APBillsPage from "./pages/accounting/ap/APBillsPage";
import VendorStatementsPage from "./pages/accounting/ap/VendorStatementsPage";
import FinancialReportsHubPage from "./pages/accounting/reports/FinancialReportsHubPage";
import IncomeStatementPage from "./pages/accounting/reports/IncomeStatementPage";
import BalanceSheetPage from "./pages/accounting/reports/BalanceSheetPage";
import CashFlowPage from "./pages/accounting/reports/CashFlowPage";
import CashForecastPage from "./pages/accounting/reports/CashForecastPage";
import BudgetVariancePage from "./pages/accounting/reports/BudgetVariancePage";
import FixedAssetsPage from "./pages/accounting/FixedAssetsPage";
import DocumentInboxPage from "./pages/accounting/DocumentInboxPage";
import AccountingPeriodClosePage from "./pages/accounting/periods/AccountingPeriodClosePage";
import ARCollectionsHubPage from "./pages/accounting/ar/ARCollectionsHubPage";
import ReadyToBillPage from "./pages/accounting/ar/ReadyToBillPage";
import ARCustomerStatementPage from "./pages/accounting/ar/ARCustomerStatementPage";
import JobOnePagerPage from "./pages/accounting/reports/JobOnePagerPage";
import Vendor1099Page from "./pages/accounting/tax/Vendor1099Page";
import SalesTaxCenterPage from "./pages/accounting/tax/SalesTaxCenterPage";
import AccountantExportPage from "./pages/accounting/export/AccountantExportPage";
import PayRunsCockpitPage from "./pages/finance/PayRunsCockpitPage";
import InstallerPayoutsPage from "./pages/finance/InstallerPayoutsPage";
import ApprovalsInboxPage from "./pages/finance/ApprovalsInboxPage";
import CollectionsWorkbenchPage from "./pages/finance/CollectionsWorkbenchPage";
import BillsPage from "./pages/finance/BillsPage";
import PurchaseOrdersPage from "./pages/finance/PurchaseOrdersPage";
import PayrollPage from "./pages/finance/PayrollPage";
import PeriodClosePage from "./pages/finance/PeriodClosePage";
import MarketingAttribution from "./pages/MarketingAttributionRoute";
import GrowthChannels from "./pages/GrowthChannels";
import Settings from "./pages/SettingsRoute";
import LeadSources from "./pages/LeadSourcesRoute";
import DispositionReport from "./pages/DispositionReportRoute";
import AlertsHistory from "./pages/AlertsHistoryRoute";
import SystemAudit from "./pages/SystemAuditRoute";
import QuotesListRoute from "./pages/QuotesListRoute";
import QuoteDetailRoute from "./pages/QuoteDetailRoute";

import CustomerPortal from "./pages/CustomerPortal";
import PipelineRoute from "./pages/PipelineRoute";
import IntelligenceDashboardRoute from "./pages/IntelligenceDashboardRoute";
import LeadDetailRoute from "./pages/LeadDetailRoute";
import MessagesPage from "./pages/MessagesPage";
import TeamMessagesFullPage from "./pages/TeamMessagesFullPage";
import LeadIntegrityRoute from "./pages/LeadIntegrityRoute";
import AutomationDashboardRoute from "./pages/AutomationDashboardRoute";
import ServiceTicketsPage from "./pages/ServiceTicketsRoute";
import ResolveDomainLanding from "./pages/resolve/ResolveDomainLanding";
import PortalsHub from "./pages/PortalsHubRoute";
import ContractorPortal from "./pages/ContractorPortal";
import InstallerExecution from "./pages/InstallerExecution";
import NotFound from "./pages/NotFound";
import PortalLogin from "./pages/PortalLogin";
import ContractorRatingPage from "./pages/ContractorRatingPage";
import IntakeReview from "./pages/IntakeReview";
import OfferingsCatalogRoute from "./pages/OfferingsCatalogRoute";
import FollowUpEngineRoute from "./pages/FollowUpEngineRoute";
import ProductRegistryRoute from "./pages/ProductRegistryRoute";
import VendorsPageRoute from "./pages/VendorsPageRoute";
import OrderingQueueRoute from "./pages/OrderingQueueRoute";
import PurchaseOrdersHub from "./pages/PurchaseOrdersHub";
import InstallReadinessRoute from "./pages/InstallReadinessRoute";
import ExecutionHubRoute from "./pages/ExecutionHubRoute";
import ExecutionTimelinePage from "./pages/execution/ExecutionTimelinePage";
import ExecutionSummaryPage from "./pages/execution/ExecutionSummaryPage";
import ExecutionControlTowerPage from "./pages/execution/ExecutionControlTowerPage";
import ExecutionQACallbacksPage from "./pages/execution/ExecutionQACallbacksPage";
import ExecutionHoldbacksPage from "./pages/execution/ExecutionHoldbacksPage";
import ExecutionPromotionQueuePage from "./pages/execution/ExecutionPromotionQueuePage";
import ExecutionInstallerNetworkPage from "./pages/execution/ExecutionInstallerNetworkPage";
import InstallersPage from "./pages/execution/InstallersPage";
import ExecutionVettingPage from "./pages/execution/ExecutionVettingPage";
import ExecutionGovernancePage from "./pages/execution/ExecutionGovernancePage";
import ExecutionInstallTodayPage from "./pages/execution/ExecutionInstallTodayPage";
import ProjectsHubRoute from "./pages/ProjectsHubRoute";
import InstallerDashboardPage from "./pages/InstallerDashboardRoute";
import DispatchIntelligenceRoute from "./pages/DispatchIntelligenceRoute";
import InstallerOffersCockpit from "./pages/InstallerOffersCockpit";
import JobOfferReviewWorkspace from "./pages/JobOfferReviewWorkspace";
import DemoLab from "./pages/DemoLab";
import RevenueIntelligenceRoute from "./pages/RevenueIntelligenceRoute";
import { TenantPortalRoute } from "@/components/portal/TenantPortalRoute";

import { FollowUpEngineProvider } from "@/data/FollowUpEngineContext";
import { OnboardingProvider } from "@/data/contractor/onboarding/OnboardingProvider";
import { FrictionLogProvider } from "@/data/FrictionLogContext";
import { OrderDraftProvider } from "@/data/OrderDraftContext";
import { OrderingPacketProvider } from "@/data/orderingPacket/context";
import { SchedulingProvider } from "@/data/scheduling/context";
import FrictionLog from "./pages/FrictionLog";
import EarningsIntelligenceRoute from "./pages/EarningsIntelligenceRoute";
import CockpitShellPreview from "./pages/CockpitShellPreview";
import SchedulingDispatchRoute from "./pages/SchedulingDispatchRoute";
import ControlPlane from "./pages/ControlPlaneRoute";
import { ControlPlaneProvider } from "@/data/controlPlane/context";
import { TenantProvider } from "@/data/tenancy/context";
import { AutomationRunnerProvider } from "@/data/AutomationRunnerContext";
import { MaverickProvider } from "@/data/maverick/context";
import { PipelineIntelligenceProvider } from "@/data/pipelineIntelligence/context";
import PipelineIntelligenceRoute from "./pages/PipelineIntelligenceRoute";
import ArchitectureHealth from "./pages/ArchitectureHealth";
import AppointmentsPageRoute from "./pages/AppointmentsPageRoute";
import CommandDashboard from "./pages/CommandDashboardRoute";
import FollowUpsPageRoute from "./pages/FollowUpsPageRoute";
import AppointmentResultsRoute from "./pages/AppointmentResultsRoute";
import AppointmentOutcome from "./pages/AppointmentOutcome";
import RepScorecardsRoute from "./pages/RepScorecardsRoute";
import CommissionsRoute from "./pages/CommissionsRoute";
import NavigationSettings from "./pages/NavigationSettingsRoute";
import AutoMappedSkuReport from "./pages/AutoMappedSkuReport";
import MigrationReviewQueue from "./pages/MigrationReviewQueue";
import LeadAppointmentImport from "./pages/LeadAppointmentImport";
import { NavigationConfigProvider } from "./data/NavigationConfigContext";
import { NavModeProvider } from "./data/NavModeContext";
import { UISkinProvider } from "./data/UISkinContext";
import { MessagingAutomationProvider } from "./data/MessagingAutomationContext";
import { ActionQueueProvider } from "@/data/actionQueue/context";
import { MaverickAssistProvider } from "@/data/maverick/assistContext";
import { InstallerExecutionProvider } from "@/data/InstallerExecutionContext";
import { JobSpecAuthoringProvider } from "@/data/JobSpecAuthoringContext";
import { useReconciliationConsumer } from "@/hooks/useReconciliationConsumer";
import { useOrganizationSyncRouter } from "@/hooks/useOrganizationSyncRouter";
import { RoleRouteGuard } from "@/components/RoleRouteGuard";
import JobSpecTemplates from "./pages/JobSpecTemplates";
import ChecklistTemplatesPage from "./pages/ChecklistTemplatesPage";
import ControlTower from "./pages/ControlTower";
import ProjectReview from "./pages/ProjectReview";
import ComplianceReviewPage from "./pages/admin/ComplianceReviewPage";
import InstallerOrganizationsPage from "./pages/admin/InstallerOrganizationsPage";
import OnboardingWizardPage from "./pages/onboarding/OnboardingWizardPage";
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 10 * 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function RoleProviderWithAuth({ children }: { children: React.ReactNode }) {
  const { role, loading } = useCurrentUser();
  console.log('ROLE_DEBUG useCurrentUser:', { role, loading });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
      </div>
    );
  }

  return <RoleProvider initialRole={role as UserRole}>{children}</RoleProvider>;
}

function AccessDeniedScreen({ role }: { role: UserRole }) {
  const navigate = useNavigate();
  const [seconds, setSeconds] = React.useState(10);
  const redirectTo = ROLE_DEFAULT_ROUTES[role] ?? "/";

  React.useEffect(() => {
    if (seconds <= 0) {
      navigate(redirectTo, { replace: true });
      return;
    }
    const t = setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [seconds, navigate, redirectTo]);

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 px-4 text-center">
      <span className="text-5xl">🔒</span>
      <h2 className="text-xl font-semibold text-foreground">Access Restricted</h2>
      <p className="max-w-xs text-sm text-muted-foreground">
        Your current role (<strong>{role}</strong>) does not have permission to view this page.
      </p>
      <div className="space-y-0.5 rounded-lg border bg-muted/40 px-8 py-3">
        <p className="text-xs text-muted-foreground">Redirecting to your default page in</p>
        <p className="text-4xl font-bold tabular-nums">{seconds}</p>
        <p className="text-xs text-muted-foreground">seconds</p>
      </div>
      <Link
        to={redirectTo}
        replace
        className="text-sm font-medium text-primary underline underline-offset-4 hover:opacity-80"
      >
        Go to my default page now
      </Link>
    </div>
  );
}

function RoleGuard({ children }: { children: React.ReactNode }) {
  // All authenticated users can access all routes.
  // Access control is enforced at the button/action level via PermissionGate.
  return <>{children}</>;
}

function RoleDataRefreshSync() {
  const { role } = useRole();
  const appData = useOptionalAppData();
  const prevRoleRef = React.useRef<UserRole | null>(null);

  React.useEffect(() => {
    if (!appData) return;
    const prev = prevRoleRef.current;
    prevRoleRef.current = role;
    if (prev && prev !== role) {
      void appData.refreshData();
    }
  }, [role, appData]);

  return null;
}

function ReconciliationSyncBootstrap() {
  useReconciliationConsumer();
  useOrganizationSyncRouter();
  return null;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/signin" replace />;
  }

  return <>{children}</>;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <AuthProvider>
      <AppDataProvider>
        <ActionQueueProvider>
        <AutomationRunnerProvider>
        <WorkflowOrchestrator />

        <RoleProviderWithAuth>
          <PermissionsProvider>
            <RoleDataRefreshSync />
            <ReconciliationSyncBootstrap />
            <NavigationConfigProvider>
              <NavModeProvider>
                <UISkinProvider>
                  <BrandingProvider>
                    <CatalogProvider>
                      <PricingProvider>
                        <InventoryProvider>
                          <ProductRegistryProvider>
                            <VendorPOProvider>
                              <WarehouseInventoryProvider>
                                <ExpenseLedgerProvider>
                                <StockLeakageProvider>
                                  <SafetyControlProvider>
                                    <PipelineConfigProvider>
                                      <FollowUpEngineProvider>
                                        <MessagingAutomationProvider>
                                          <FrictionLogProvider>
                                            <OrderDraftProvider>
                                              <OrderingPacketProvider>
                                                <SchedulingProvider>
                                                  <ControlPlaneProvider>
                                                    <TenantProvider>
                                                      <InstallerExecutionProvider>
                                                        <JobSpecAuthoringProvider>
                                                          <OnboardingProvider>
                                                           <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                                                            <GeneralLedgerProvider>
                                                            <AutoPostingBridge />
                                                            <ChangeOrderPostingBridge />
                                                            <RecurringJournalProvider>
                                                            <FixedAssetsProvider>
                                                            <BudgetsProvider>
                                                            <DocumentInboxProvider>
                                                            <BankFeedProvider>
                                                            <APProvider>
                                                            <AccountingTourProvider>
                                                            <MaverickProvider>
                                                              <MaverickAssistProvider>
                                                                <PipelineIntelligenceProvider>
                                                                 <CinematicProvider>
                                                                   <RoleRouteGuard />
                                                                   {/* Portal routes — separate layout, no internal nav */}
                                                                   <Routes>
                                                                    <Route path="/signin" element={<SignIn />} />
                                                                    <Route path="/sign-in" element={<Navigate to="/signin" replace />} />
                                                                    <Route path="/portal/login" element={<PortalLogin />} />
                                                                    <Route path="/onboarding/:token" element={<OnboardingWizardPage />} />
                                                                    <Route path="/portal" element={<CustomerPortal />} />
                                                                    <Route path="/portal/:jobId" element={<CustomerPortal />} />
                                                                    <Route path="/portal/:jobId/rate-contractor" element={<ContractorRatingPage />} />
                                                                    {/* White-labeled tenant portal routes */}
                                                                    <Route path="/cx/:tenantSlug" element={<TenantPortalRoute />} />
                                                                    <Route path="/cx/:tenantSlug/:jobId" element={<TenantPortalRoute />} />
                                                                    {/* Full-page messaging — no AppLayout chrome */}
                                                                    <Route path="/team-messages" element={<TeamMessagesFullPage />} />

                                                                    <Route path="/*" element={
                                                                      <ProtectedRoute>
                                                                      <RoleGuard>
                                                                      <AppLayout>
                                                                        <ProcurementPostingBridge />
                                                                        <InstallerReimbursementPostingBridge />
                                                                        <TourOverlay />
                                                                        <TourSpotlight />
                                                                        <TourPromptCard />
                                                                        <Routes>
                                                                          <Route path="/" element={<Dashboard />} />
                                                                          <Route path="/intake-review" element={<IntakeReview />} />
                                                                          <Route path="/pipeline" element={<PipelineRoute />} />
                                                                          <Route path="/pipeline/:id" element={<LeadDetailRoute />} />
                                                                          <Route path="/lead-integrity" element={<LeadIntegrityRoute />} />
                                                                          <Route path="/messages" element={<MessagesPage />} />
                                                                          <Route path="/opportunities" element={<QuotesListRoute />} />
                                                                          <Route path="/opportunities/:id" element={<QuoteDetailRoute />} />
                                                                          <Route path="/projects" element={<ProjectsHubRoute />} />
                                                                          <Route path="/jobs" element={<JobsListRoute />} />
                                                                          <Route path="/jobs/:id" element={<JobDetail />} />
                                                                          <Route path="/issues" element={<IssuesListRoute />} />
                                                                          <Route path="/issues/:issueId" element={<IssuesListRoute />} />
                                                                          <Route path="/contractors" element={<ContractorsListRoute />} />
                                                                          <Route path="/contractors/:id" element={<ContractorProfile />} />
                                                                          <Route path="/customers" element={<CustomersListRoute />} />
                                                                          <Route path="/customers/:name" element={<CustomerProfile />} />
                                                                          <Route path="/contractor-onboarding" element={<ContractorOnboardingRoute />} />
                                                                          <Route path="/contractor-intake" element={<ContractorIntakeRoute />} />
                                                                          <Route path="/reports" element={<Reports />} />
                                                                          <Route path="/qa-form" element={<QAForm />} />
                                                                          <Route path="/qa/:jobId" element={<QAForm />} />
                                                                          <Route path="/contractor-portal" element={<ContractorPortal />} />
                                                                          {/* /installer-portal — alias for installers; same component, post-rename naming */}
                                                                          <Route path="/installer-portal" element={<ContractorPortal />} />
                                                                          <Route path="/installer-execution/:jobId" element={<InstallerExecution />} />
                                                                          <Route path="/scheduling" element={<SchedulingRoute />} />
                                                                          <Route path="/import/review-queue" element={<MigrationReviewQueue />} />
                                                                          <Route path="/import/leads-appointments" element={<LeadAppointmentImport />} />
                                                                          <Route path="/issue-form" element={<IssueForm />} />
                                                                          <Route path="/permitting" element={<PermittingPortalRoute />} />
                                                                          <Route path="/warehouse" element={<WarehouseReadinessRoute />} />
                                                                          <Route path="/stock-control" element={<StockControlRoute />} />
                                                                          <Route path="/closeout" element={<ProjectCloseoutRoute />} />
                                                                          <Route path="/inventory" element={<InventoryModuleRoute />} />
                                                                          <Route path="/accounting" element={<AccountingViewRoute />} />
                                                                          <Route path="/accounting/gl" element={<GeneralLedgerHubPage />} />
                                                                          <Route path="/accounting/gl/chart-of-accounts" element={<ChartOfAccountsPage />} />
                                                                          <Route path="/accounting/gl/journal-entries" element={<JournalEntriesPage />} />
                                                                          <Route path="/accounting/gl/trial-balance" element={<TrialBalancePage />} />
                                                                          <Route path="/accounting/gl/detail" element={<GeneralLedgerDetailPage />} />
                                                                          <Route path="/accounting/gl/audit-log" element={<AuditLogPage />} />
                                                                          <Route path="/accounting/gl/recurring" element={<RecurringJournalsPage />} />
                                                                          <Route path="/accounting/gl/review-queue" element={<JournalReviewQueuePage />} />
                                                                          <Route path="/accounting/settings" element={<AccountingSettingsPage />} />
                                                                          <Route path="/accounting/close-readiness" element={<CloseReadinessPage />} />
                                                                          <Route path="/accounting/reports/wip-aging" element={<WipAgingPage />} />
                                                                          <Route path="/accounting/reports/job-profitability" element={<JobProfitabilityPage />} />
                                                                          <Route path="/accounting/bank-rec" element={<BankReconciliationHubPage />} />
                                                                          <Route path="/accounting/bank-rec/:glAccountId" element={<BankReconciliationPage />} />
                                                                          <Route path="/accounting/ap" element={<APBillsPage />} />
                                                                          <Route path="/accounting/ap/vendor-statements" element={<VendorStatementsPage />} />
                                                                          <Route path="/accounting/reports" element={<FinancialReportsHubPage />} />
                                                                          <Route path="/accounting/reports/income-statement" element={<IncomeStatementPage />} />
                                                                          <Route path="/accounting/reports/balance-sheet" element={<BalanceSheetPage />} />
                                                                          <Route path="/accounting/reports/cash-flow" element={<CashFlowPage />} />
                                                                          <Route path="/accounting/reports/cash-forecast" element={<CashForecastPage />} />
                                                                          <Route path="/accounting/reports/budget-variance" element={<BudgetVariancePage />} />
                                                                          <Route path="/accounting/fixed-assets" element={<FixedAssetsPage />} />
                                                                          <Route path="/accounting/document-inbox" element={<DocumentInboxPage />} />
                                                                          <Route path="/accounting/periods" element={<AccountingPeriodClosePage />} />
                                                                          <Route path="/accounting/ar" element={<ARCollectionsHubPage />} />
                                                                          <Route path="/accounting/ar/ready-to-bill" element={<ReadyToBillPage />} />
                                                                          <Route path="/accounting/ar/:customerId" element={<ARCustomerStatementPage />} />
                                                                          <Route path="/accounting/reports/job-profitability/:jobId" element={<JobOnePagerPage />} />
                                                                          <Route path="/accounting/1099" element={<Vendor1099Page />} />
                                                                          <Route path="/accounting/sales-tax" element={<SalesTaxCenterPage />} />
                                                                          <Route path="/accounting/export" element={<AccountantExportPage />} />
                                                                          <Route path="/finance/pay-runs" element={<PayRunsCockpitPage />} />
                                                                          <Route path="/finance/payouts" element={<InstallerPayoutsPage />} />
                                                                          <Route path="/finance/approvals" element={<ApprovalsInboxPage />} />
                                                                          <Route path="/finance/collections" element={<CollectionsWorkbenchPage />} />
                                                                          <Route path="/finance/bills" element={<BillsPage />} />
                                                                          <Route path="/finance/purchase-orders" element={<PurchaseOrdersPage />} />
                                                                          <Route path="/finance/payroll" element={<PayrollPage />} />
                                                                          <Route path="/finance/close" element={<PeriodClosePage />} />
                                                                          <Route path="/marketing-attribution" element={<MarketingAttribution />} />
                                                                          <Route path="/growth-channels" element={<GrowthChannels />} />
                                                                          <Route path="/lead-sources" element={<LeadSources />} />
                                                                          <Route path="/disposition-report" element={<DispositionReport />} />
                                                                          <Route path="/appointment-outcome/:appointmentResultId" element={<AppointmentOutcome />} />
                                                                          <Route path="/alerts" element={<AlertsHistory />} />
                                                                          <Route path="/system-audit" element={<SystemAudit />} />
                                                                          <Route path="/settings" element={<Settings />} />
                                                                          <Route path="/offerings" element={<OfferingsCatalogRoute />} />
                                                                          <Route path="/follow-up-engine" element={<FollowUpEngineRoute />} />
                                                                          <Route path="/product-registry" element={<ProductRegistryRoute />} />
                                                                          <Route path="/jobspec-templates" element={<JobSpecTemplates />} />
                                                                          <Route path="/checklist-templates" element={<ChecklistTemplatesPage />} />
                                                                          <Route path="/vendors" element={<VendorsPageRoute />} />
                                                                          <Route path="/ordering-queue" element={<OrderingQueueRoute />} />
                                                                          <Route path="/purchase-orders" element={<PurchaseOrdersHub />} />
                                                                          <Route path="/install-readiness" element={<InstallReadinessRoute />} />
                                                                          <Route path="/execution" element={<ExecutionHubRoute />} />
                                                                          <Route path="/execution/control-tower" element={<ExecutionControlTowerPage />} />
                                                                          <Route path="/execution/timeline" element={<ExecutionTimelinePage />} />
                                                                          <Route path="/execution/summary" element={<ExecutionSummaryPage />} />
                                                                          <Route path="/execution/qa-callbacks" element={<ExecutionQACallbacksPage />} />
                                                                          <Route path="/execution/holdbacks" element={<ExecutionHoldbacksPage />} />
                                                                          <Route path="/execution/promotion-queue" element={<ExecutionPromotionQueuePage />} />
                                                                          <Route path="/execution/installer-network" element={<ExecutionInstallerNetworkPage />} />
                                                                          <Route path="/execution/installers" element={<InstallersPage />} />
                                                                          <Route path="/execution/vetting/:contractorId" element={<ExecutionVettingPage />} />
                                                                          <Route path="/execution/governance" element={<ExecutionGovernancePage />} />
                                                                          <Route path="/execution/install-today" element={<ExecutionInstallTodayPage />} />
                                                                          <Route path="/execution/installs-live" element={<Navigate to="/execution/install-today?tab=active-installs" replace />} />
                                                                          <Route path="/resolve" element={<ResolveDomainLanding />} />
                                                                          <Route path="/installer-dashboard" element={<InstallerDashboardPage />} />
                                                                          <Route path="/automations" element={<AutomationDashboardRoute />} />
                                                                          <Route path="/earnings-intelligence" element={<EarningsIntelligenceRoute />} />
                                                                          <Route path="/scheduling-dispatch" element={<SchedulingDispatchRoute />} />
                                                                          <Route path="/dispatch-intelligence" element={<DispatchIntelligenceRoute />} />
                                                                          <Route path="/installer-offers" element={<InstallerOffersCockpit />} />
                                                                          <Route path="/installer-offers/:jobId/review" element={<JobOfferReviewWorkspace />} />
                                                                          <Route path="/friction-log" element={<FrictionLog />} />
                                                                          <Route path="/control-plane" element={<ControlPlane />} />
                                                                          <Route path="/intelligence" element={<IntelligenceDashboardRoute />} />
                                                                          <Route path="/service-tickets" element={<ServiceTicketsPage />} />
                                                                          <Route path="/portals" element={<PortalsHub />} />
                                                                          <Route path="/demo-lab" element={<DemoLab />} />
                                                                          <Route path="/revenue-intelligence" element={<RevenueIntelligenceRoute />} />
                                                                          <Route path="/pipeline-intelligence" element={<PipelineIntelligenceRoute />} />
                                                                          <Route path="/architecture-health" element={<ArchitectureHealth />} />
                                                                          <Route path="/appointments" element={<AppointmentsPageRoute />} />
                                                                          <Route path="/appointments/:id" element={<QuoteDetailRoute />} />
                                                                          <Route path="/follow-ups" element={<FollowUpsPageRoute />} />
                                                                          <Route path="/appointment-results" element={<AppointmentResultsRoute />} />
                                                                          <Route path="/rep-scorecards" element={<RepScorecardsRoute />} />
                                                                          <Route path="/commissions" element={<CommissionsRoute />} />
                                                                          <Route path="/command-dashboard" element={<CommandDashboard />} />
                                                                          <Route path="/navigation-settings" element={<NavigationSettings />} />
                                                                          <Route path="/product-registry/auto-mapped-skus" element={<AutoMappedSkuReport />} />
                                                                          <Route path="/cockpit-shell-preview" element={<CockpitShellPreview />} />
                                                                          <Route path="/admin/control-tower" element={<ControlTower />} />
                                                                          <Route path="/admin/project-review" element={<ProjectReview />} />
                                                                          <Route path="/admin/compliance-review" element={<ComplianceReviewPage />} />
                                                                          <Route path="/admin/installer-organizations" element={<InstallerOrganizationsPage />} />
                                                                          <Route path="*" element={<NotFound />} />
                                                                        </Routes>
                                                                      </AppLayout>
                                                                      </RoleGuard>
                                                                      </ProtectedRoute>
                                                                    } />
                                                                  </Routes>
                                                                </CinematicProvider>
                                                                </PipelineIntelligenceProvider>
                                                              </MaverickAssistProvider>
                                                            </MaverickProvider>
                                                            </AccountingTourProvider>
                                                            </APProvider>
                                                            </BankFeedProvider>
                                                            </DocumentInboxProvider>
                                                            </BudgetsProvider>
                                                            </FixedAssetsProvider>
                                                            </RecurringJournalProvider>
                                                            </GeneralLedgerProvider>
                                                          </BrowserRouter>
                                                          </OnboardingProvider>
                                                        </JobSpecAuthoringProvider>
                                                      </InstallerExecutionProvider>
                                                    </TenantProvider>
                                                  </ControlPlaneProvider>
                                                </SchedulingProvider>
                                              </OrderingPacketProvider>
                                            </OrderDraftProvider>
                                          </FrictionLogProvider>
                                        </MessagingAutomationProvider>
                                      </FollowUpEngineProvider>
                                    </PipelineConfigProvider>
                                  </SafetyControlProvider>
                                </StockLeakageProvider>
                                </ExpenseLedgerProvider>
                              </WarehouseInventoryProvider>
                            </VendorPOProvider>
                          </ProductRegistryProvider>
                        </InventoryProvider>
                      </PricingProvider>
                    </CatalogProvider>
                  </BrandingProvider>
                </UISkinProvider>
              </NavModeProvider>
            </NavigationConfigProvider>
          </PermissionsProvider>
          </RoleProviderWithAuth>
      </AutomationRunnerProvider>
      </ActionQueueProvider>
      </AppDataProvider>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
